import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import CloakGate from "@/components/CloakGate";
import Index from "./pages/Index.tsx";
import FAQ from "./pages/FAQ.tsx";
import IssueCard from "./pages/IssueCard.tsx";
import Success from "./pages/Success.tsx";
import NotFound from "./pages/NotFound.tsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.tsx";
import TermsOfService from "./pages/TermsOfService.tsx";
import CookiePolicy from "./pages/CookiePolicy.tsx";
import AmlPolicy from "./pages/AmlPolicy.tsx";
import HelpCenter from "./pages/HelpCenter.tsx";
import ContactUs from "./pages/ContactUs.tsx";
import StatusPage from "./pages/StatusPage.tsx";
import Security from "./pages/Security.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import { captureMetaTrackingParams, trackMetaPixelPageView } from "./lib/metaPixel.ts";

const queryClient = new QueryClient();

const MetaPixelTracker = () => {
  const location = useLocation();

  useEffect(() => {
    captureMetaTrackingParams();
    trackMetaPixelPageView();
  }, [location.pathname, location.search]);

  return null;
};

const AppRoutes = () => (
  <LanguageProvider>
    <MetaPixelTracker />
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/:lang" element={<Index />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/:lang/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/:lang/cookies" element={<CookiePolicy />} />
      <Route path="/aml" element={<AmlPolicy />} />
      <Route path="/:lang/aml" element={<AmlPolicy />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/:lang/help" element={<HelpCenter />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/:lang/contact" element={<ContactUs />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/:lang/status" element={<StatusPage />} />
      <Route path="/security" element={<Security />} />
      <Route path="/:lang/security" element={<Security />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/:lang/faq" element={<FAQ />} />
      <Route path="/issue-card" element={<IssueCard />} />
      <Route path="/:lang/issue-card" element={<IssueCard />} />
      <Route path="/success" element={<Success />} />
      <Route path="/:lang/success" element={<Success />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </LanguageProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CloakGate>
          <AppRoutes />
        </CloakGate>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
